import Button from "@atoms/button";
import { Input } from "@atoms/input";
import { Loader } from "@atoms/loader/loader";
import { Typography } from "@atoms/typography";
import React, { FC, useMemo } from "react";
import styled from "styled-components";
import { BookingContactFormData } from "./types";
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  useForm,
} from "react-hook-form";
import { ContactSectionFormProps } from "@organisms/contact-section";
import { Trans, useTranslation } from "next-i18next";

function InputCheckIn({
  sending,
  field,
  fieldState,
  endDate,
}: {
  sending: boolean;
  fieldState: ControllerFieldState;
  field: ControllerRenderProps<BookingContactFormData, "arrival_date">;
  endDate?: Date | string | null;
}) {
  const { t } = useTranslation("forms");
  const { onChange, value, ...rest } = field;
  const selected = useMemo(
    () => (typeof value === "string" ? new Date(value) : value),
    [value]
  );
  return (
    <Input
      placeholderText={t("arrival_date")}
      type="datepicker"
      endAddorment="calendar"
      className="form-check-in"
      title="Arrival date"
      disabled={sending}
      onChange={(value: any) => onChange({ target: { value } })}
      selected={selected}
      startDate={selected}
      selectsStart
      endDate={typeof endDate === "string" ? new Date(endDate) : endDate}
      error={!!fieldState.error}
      errorMessage={fieldState.error?.message}
      {...rest}
    />
  );
}

function InputCheckOut({
  sending,
  field,
  fieldState,
  startDate,
}: {
  sending: boolean;
  fieldState: ControllerFieldState;
  field: ControllerRenderProps<BookingContactFormData, "departure_date">;
  startDate?: Date | string | null;
}) {
  const { t } = useTranslation("forms");
  const { onChange, value, ...rest } = field;
  const selected = useMemo(
    () => (typeof value === "string" ? new Date(value) : value),
    [value]
  );
  const checkInDate =
    typeof startDate === "string" ? new Date(startDate) : startDate;
  const minDate = checkInDate ?? new Date();
  return (
    <Input
      placeholderText={t("departure_date")}
      type="datepicker"
      endAddorment="calendar"
      className="form-check-out"
      title={t("departure_date")}
      disabled={sending}
      onChange={(value: any) => onChange({ target: { value } })}
      selected={selected}
      endDate={selected}
      startDate={checkInDate}
      minDate={minDate}
      selectsEnd
      error={!!fieldState.error}
      errorMessage={fieldState.error?.message}
      {...rest}
    />
  );
}

export const BookingContactForm: FC<
  ContactSectionFormProps<BookingContactFormData>
> = ({ sending, onSubmit, initialData }) => {
  const { t } = useTranslation();
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<BookingContactFormData>();

  const guestsOptions = useMemo(
    () => [
      { value: "1", label: "1 " + t("guest", { count: 1 }) },
      { value: "2", label: "2 " + t("guest", { count: 2 }) },
      { value: "3", label: "3 " + t("guest", { count: 3 }) },
      { value: "4", label: "4 " + t("guest", { count: 4 }) },
      { value: "5", label: "5 " + t("guest", { count: 5 }) },
      { value: "6", label: "6 " + t("guest", { count: 6 }) },
      { value: "7", label: "7 " + t("guest", { count: 7 }) },
      { value: "8", label: "8 " + t("guest", { count: 8 }) },
      { value: "9", label: "9 " + t("guest", { count: 9 }) },
      { value: "10", label: "10 " + t("guest", { count: 10 }) },
      { value: "11", label: "11 " + t("guest", { count: 11 }) },
      { value: "12", label: "12 " + t("guest", { count: 12 }) },
      { value: "13", label: "13 " + t("guest", { count: 13 }) },
      { value: "14", label: "14 " + t("guest", { count: 14 }) },
      { value: "15", label: "15 " + t("guest", { count: 15 }) },
      { value: "16", label: "16 " + t("guest", { count: 16 }) },
      { value: "17", label: "17 " + t("guest", { count: 17 }) },
      { value: "18", label: "18 " + t("guest", { count: 18 }) },
      { value: "19", label: "19 " + t("guest", { count: 19 }) },
      { value: "20", label: "20 " + t("guest", { count: 20 }) },
    ],
    [t]
  );

  const [checkIn, checkOut] = watch(["arrival_date", "departure_date"]);

  return (
    <Container>
      <Typography variant="title2">
        <Trans>CAN&apos;T YOU FIND WHAT YOU WERE LOOKING FOR?</Trans>
      </Typography>
      <Typography variant="body1" paragraph>
        <Trans>
          At Feeling Home, we have a wide selection of great houses in Mallorca,
          some of them may not be visible yet at our website.
        </Trans>
      </Typography>
      <Typography variant="body1" paragraph>
        <Trans>
          If you are looking for something in particular which you can&apos;t
          see here, please just fill this simple form:
        </Trans>
      </Typography>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Input
          placeholder="Name"
          type="text"
          endAddorment="user"
          className="form-name"
          disabled={sending}
          error={!!errors.name}
          errorMessage={errors.name?.message}
          {...register("name", {
            required: t("validation.email.required", {
              defaultValue: "Name is required",
            }).toString(),
          })}
        />
        <Input
          placeholder="E-Mail"
          type="email"
          endAddorment="at-sign"
          className="form-email"
          disabled={sending}
          error={!!errors.email}
          errorMessage={errors.email?.message}
          {...register("email", {
            pattern: {
              value: /^.+?@.+?\..+?$/i,
              message: t("validation.email.format", {
                defaultValue: "Email format is not valid",
              }).toString(),
            },
          })}
        />

        <Input
          placeholder="Phone"
          type="tel"
          endAddorment="mobile"
          className="form-phone"
          disabled={sending}
          error={!!errors.phone}
          errorMessage={errors.phone?.message}
          {...register("phone")}
        />

        <Controller
          name="arrival_date"
          control={control}
          defaultValue={initialData?.arrival_date}
          rules={{
            required: t("validation.checkin.required", {
              defaultValue: "Arrival date / check-in is required",
            }).toString(),
          }}
          render={({ field, fieldState }) => (
            <InputCheckIn
              endDate={checkOut}
              fieldState={fieldState}
              field={field}
              sending={sending}
            />
          )}
        />

        <Controller
          name="departure_date"
          control={control}
          defaultValue={initialData?.departure_date}
          rules={{
            required: t("validation.checkout.required", {
              defaultValue: "Check-out is required",
            }).toString(),
          }}
          render={({ field, fieldState }) => (
            <InputCheckOut
              startDate={checkIn}
              fieldState={fieldState}
              field={field}
              sending={sending}
            />
          )}
        />
        <Input
          type="select"
          options={guestsOptions}
          className="form-guests"
          title="Guests"
          disabled={sending}
          defaultValue={initialData?.guests}
          {...register("guests")}
        />
        <Input
          type="textarea"
          className="form-message"
          placeholder="Message"
          rows={6}
          disabled={sending}
          error={!!errors.message}
          errorMessage={errors.message?.message}
          {...register("message", {
            required: t("validation.message.required", {
              defaultValue: "Message is required",
            }).toString(),
          })}
        />
        <div className="form-action">
          <Button variant="action" disabled={sending}>
            {sending && (
              <div className="btn-loader">
                <Loader />
              </div>
            )}
            <Trans>SEND</Trans>
          </Button>
        </div>
      </Form>
    </Container>
  );
};

const Container = styled.div``;

const Form = styled.form`
  display: grid;
  gap: 1em;
  grid-template-columns: 1fr;
  grid-template-areas:
    "form-name"
    "form-email"
    "form-phone"
    "form-check-in"
    "form-check-out"
    "form-guests"
    "form-message"
    "form-action";

  .form-name {
    grid-area: form-name;
  }
  .form-email {
    grid-area: form-email;
  }
  .form-phone {
    grid-area: form-phone;
  }
  .form-check-in {
    grid-area: form-check-in;
  }
  .form-check-out {
    grid-area: form-check-out;
  }
  .form-guests {
    grid-area: form-guests;
  }
  .form-message {
    grid-area: form-message;
  }
  .form-action {
    grid-area: form-action;
    display: flex;
    justify-content: flex-end;

    button {
      font-size: 1.2em;
      min-width: 9em;
      justify-content: center;
      align-items: center;
      position: relative;
      letter-spacing: 0.5px;

      > .btn-loader {
        position: absolute;
        top: 0;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }

  @media (min-width: 48em) {
    grid-template-columns: repeat(6, 1fr);
    grid-template-areas:
      "form-name form-name form-email form-email form-phone form-phone"
      "form-check-in form-check-in form-check-out form-check-out form-guests form-guests"
      "form-message form-message form-message form-message form-message form-message"
      "form-action form-action form-action form-action form-action form-action";
  }
`;
